import { Box, Theme, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { dayjs, formatDateWithWeekday, formatTime } from "@outschool/time";
import { Input } from "@outschool/ui-legacy-component-library";
import React from "react";

export const PADDING_X_MOBILE = "1em";
export const PADDING_X_DESKTOP = "1.5em";

export default function AvailableTimeSelector({
  availableTimes,
  selectedDateTime,
  hasSelectedTime,
  timeZone,
  handleTimeChange,
}: {
  availableTimes: string[];
  selectedDateTime: Date | undefined;
  hasSelectedTime: boolean;
  timeZone: string;
  handleTimeChange: (date: Date) => void;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\activityEnrollableTimes\\AvailableTimeSelector",
  );
  const isSelected = (time: string) =>
    hasSelectedTime && dayjs(selectedDateTime).isSame(time);

  const selectedStyles = {
    borderColor: "primary.800",
    backgroundColor: "primary.800",
    color: "white",
  };

  return (
    <Box
      sx={(theme: Theme) => ({
        gridArea: "time-selector",
        flexGrow: 1,
        maxHeight: "inherit",
        overflowY: "scroll",
        paddingX: PADDING_X_MOBILE,
        paddingBottom: "1em",
        [theme.breakpoints.up("md")]: {
          paddingX: "0",
        },
      })}
    >
      <Typography
        component="div"
        sx={(theme: Theme) => ({
          marginBottom: "0.5em",
          [theme.breakpoints.up("md")]: {
            marginBottom: "1em",
            textAlign: "center",
          },

          fontWeight: "fontWeightBold",
        })}
      >
        {formatDateWithWeekday(selectedDateTime, timeZone)}
      </Typography>
      {availableTimes?.length === 0 ? (
        <Typography>{t`No available times for the selected date.`}</Typography>
      ) : (
        <Box
          sx={(theme: Theme) => ({
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",

            [theme.breakpoints.up("md")]: {
              gridTemplateColumns: "1fr",
            },
            gap: "4px",
          })}
        >
          {availableTimes.map((time, idx) => (
            <label style={{ marginBottom: 0 }} key={idx} htmlFor={time}>
              <Box
                sx={{
                  textWrap: "nowrap",
                  border: "2px solid",
                  borderRadius: "999px",
                  borderColor: "neutral.200",
                  padding: "6px",
                  textAlign: "center",
                  fontWeight: 500,
                  "&:hover": !isSelected(time)
                    ? {
                        borderColor: "primary.800",
                        backgroundColor: "neutral.100",
                        color: "neutral.900",
                        cursor: "pointer",
                      }
                    : {},
                  "&:focus-within": !isSelected(time)
                    ? {
                        borderColor: "primary.800",
                        backgroundColor: "neutral.100",
                        color: "neutral.900",
                        outlineOffset: "2px",
                        outline: "2px solid",
                        outlineColor: "primary.800",
                      }
                    : {},
                  ...(isSelected(time) && selectedStyles),
                }}
              >
                <Input
                  id={time}
                  type="radio"
                  name="time"
                  value={time}
                  checked={isSelected(time)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleTimeChange(new Date(e.target.id))
                  }
                  sx={{ position: "fixed", opacity: 0 }}
                />
                <Typography
                  sx={(theme: Theme) => ({
                    fontWeight: 500,
                    fontSize: theme.typography.pxToRem(14),
                    [theme.breakpoints.up("md")]: {
                      fontSize: theme.typography.pxToRem(16),
                    },
                  })}
                >
                  {formatTime(time, timeZone, false, true)}
                </Typography>
              </Box>
            </label>
          ))}
        </Box>
      )}
    </Box>
  );
}
